import { workflowPayloadFromWorkflow } from "components/admin/tenant/workflow/workflow-edit";
import { SupportEmailLink } from "components/app/support-email-link";
import { ContentFull, ContentHeader } from "components/layout";
import { Badge } from "components/ui/badge";
import { Breadcrumbs } from "components/ui/breadcrumbs";
import { InlineBanner } from "components/ui/inline-banner";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import {
    CustomListKey,
    GetWorkflowSettingsResult,
    useSetWorkflowSettingMutation,
} from "generated/graphql";
import React from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { loadTenant, navigate } from "store/actions";
import { useProductName } from "store/selectors/branding";
import { useCustomList } from "store/selectors/bridge";
import { useCrmMode } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { EditableWorkflow } from "../types";
import {
    blueprintChangeEventProperties,
    WizardStepArrival,
    wizardStepViewEventProperties,
    workflowEventProperties,
} from "./builder-analytics";
import { actionContinueBlocker } from "./builder-helper";
import { useWorkflowEditor } from "./use-workflow-editor";
import { WizardActions } from "./wizard-actions";
import { WizardBasics } from "./wizard-basics";
import { WizardReview } from "./wizard-review";
import { WizardStepper } from "./wizard-stepper";
import {
    applyBlueprint,
    BlueprintContext,
    blueprintResetNotice,
    BlueprintResult,
    detectBlueprint,
    generateActionId,
    sameBlueprint,
    WorkflowBlueprint,
} from "./workflow-blueprint";
import {
    useWorkflowStatusActions,
    WorkflowStatusActions,
} from "./workflow-status-actions";

const WIZARD_STEPS = ["Basics", "Actions", "Review"] as const;
const ACTIONS_INDEX = WIZARD_STEPS.indexOf("Actions");

interface WorkflowWizardProps {
    workflow: EditableWorkflow;
    workflowSettings: GetWorkflowSettingsResult;
    refetch: () => Promise<void>;
}

export const WorkflowWizard: React.FC<WorkflowWizardProps> = props => {
    const { workflow, workflowSettings, refetch } = props;

    const [stepIndex, setStepIndex] = React.useState(0);
    const [resetNotice, setResetNotice] = React.useState<string | null>(null);
    const applyInFlight = React.useRef(false);
    const productName = useProductName();
    const { mode } = useCrmMode();
    const globalDispatch = useDispatch();
    const editor = useWorkflowEditor(workflow, refetch);
    const { readPayload, editPayload, saving } = editor;
    const setWorkflowSettingMutation = useSetWorkflowSettingMutation();
    const { customList: clientStatusCustomList } = useCustomList(
        CustomListKey.ClientStatus,
    );
    const { customList: homeCareLeadStatusCustomList } = useCustomList(
        CustomListKey.HomeCareLeadStatus,
    );

    const blueprintContext = React.useMemo<BlueprintContext>(
        () => ({
            clientStatusCustomList: clientStatusCustomList ?? null,
            homeCareLeadStatusCustomList: homeCareLeadStatusCustomList ?? null,
            newActionId: generateActionId,
        }),
        [clientStatusCustomList, homeCareLeadStatusCustomList],
    );

    const currentPayload = React.useMemo(
        () => workflowPayloadFromWorkflow(workflow),
        [workflow],
    );
    const blueprint = React.useMemo(
        () => detectBlueprint(currentPayload),
        [currentPayload],
    );
    const eventProperties = React.useMemo(
        () => workflowEventProperties(workflow, blueprint),
        [workflow, blueprint],
    );
    const actions = useWorkflowStatusActions({
        workflow,
        editor,
        eventProperties,
        refetch,
    });
    const { editable, isDraft, publishBlocker } = actions;
    const continueBlocker = React.useMemo(
        () =>
            editable && stepIndex === ACTIONS_INDEX
                ? actionContinueBlocker(currentPayload)
                : null,
        [editable, stepIndex, currentPayload],
    );
    const canEditStructure = editable && isDraft && !!blueprint;
    const storedSettings = React.useMemo(
        () =>
            workflowSettings.tenant
                .filter(value => value.workflowId === workflow.id)
                .map(value => ({ key: value.key, textValue: value.textValue ?? "" })),
        [workflowSettings.tenant, workflow.id],
    );

    const arrivalReported = React.useRef(false);

    React.useEffect(() => {
        if (arrivalReported.current) {
            return;
        }
        arrivalReported.current = true;
        handleEvent(
            "workflow-builder-step-view",
            wizardStepViewEventProperties(workflow, blueprint, {
                wizardStep: WIZARD_STEPS[0],
                arrivedBy: "open",
            }),
        );
    }, [workflow, blueprint]);

    const goToStep = React.useCallback(
        (index: number, arrivedBy: WizardStepArrival) => {
            setStepIndex(index);
            handleEvent(
                "workflow-builder-step-view",
                wizardStepViewEventProperties(workflow, blueprint, {
                    wizardStep: WIZARD_STEPS[index],
                    arrivedBy,
                }),
            );
        },
        [workflow, blueprint],
    );

    const handleStepperSelect = React.useCallback(
        (index: number) => goToStep(index, "stepper"),
        [goToStep],
    );

    const onChangeTrigger = React.useCallback(
        (newWorkflow: EditableWorkflow) => {
            const { triggerInput } = workflowPayloadFromWorkflow(newWorkflow);
            handleEvent("workflow-builder-edit", {
                ...eventProperties,
                field: "trigger",
            });
            editPayload(payload => ({ ...payload, triggerInput }));
        },
        [editPayload, eventProperties],
    );

    const onChangeAction = React.useCallback(
        (newWorkflow: EditableWorkflow) => {
            const { actionEntities, actorMode, defaultActorId } =
                workflowPayloadFromWorkflow(newWorkflow);
            handleEvent("workflow-builder-edit", {
                ...eventProperties,
                field: "action",
            });
            editPayload(payload => ({
                ...payload,
                actionEntities,
                actorMode,
                defaultActorId,
            }));
        },
        [editPayload, eventProperties],
    );

    const persistBlueprint = React.useCallback(
        async (result: BlueprintResult) => {
            try {
                await editPayload(() => result.payload);
                await Promise.all(
                    result.settingResets.map(reset =>
                        setWorkflowSettingMutation.mutateAsync({
                            params: {
                                workflowId: workflow.id,
                                key: reset.key,
                                textValue: reset.textValue,
                            },
                        }),
                    ),
                );
                if (result.settingResets.length > 0) {
                    loadTenant(globalDispatch);
                }
            } finally {
                refetch();
            }
        },
        [editPayload, setWorkflowSettingMutation, workflow.id, globalDispatch, refetch],
    );

    const handleApplyBlueprint = React.useCallback(
        async (target: WorkflowBlueprint) => {
            if (!blueprint || !editable || !isDraft || applyInFlight.current) {
                return;
            }
            if (sameBlueprint(blueprint, target)) {
                return;
            }
            const result = applyBlueprint(
                readPayload(),
                storedSettings,
                target,
                blueprintContext,
            );
            if (!result) {
                return;
            }
            applyInFlight.current = true;
            setResetNotice(
                blueprintResetNotice(
                    result,
                    blueprint.entityType,
                    target.entityType,
                    mode,
                ),
            );
            handleEvent("workflow-builder-blueprint-change", {
                ...blueprintChangeEventProperties(workflow.id, blueprint, target),
                settingsResetCount: result.settingResets.length,
            });
            try {
                await persistBlueprint(result);
            } finally {
                applyInFlight.current = false;
            }
        },
        [
            blueprint,
            editable,
            isDraft,
            readPayload,
            storedSettings,
            blueprintContext,
            mode,
            persistBlueprint,
            workflow.id,
        ],
    );

    const handlePublishAndFinish = React.useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            const published = await actions.publish("review");
            if (published) {
                navigate(`/settings/workflow/${workflow.id}`);
            }
        },
        [actions, workflow.id],
    );

    const handleShowActions = React.useCallback(
        () => goToStep(ACTIONS_INDEX, "blocker"),
        [goToStep],
    );

    const stepProps = {
        workflow,
        workflowSettings,
        editable,
        isDraft,
        canEditStructure,
        publishBlocker,
        onShowActions: handleShowActions,
        onChangeTrigger,
        onChangeAction,
        editPayload,
        refetch,
        blueprint: {
            blueprint,
            context: blueprintContext,
            onApply: handleApplyBlueprint,
        },
    };

    return (
        <ContentFull>
            <div className="space-y-4">
                <div>
                    <Breadcrumbs
                        links={[
                            { label: "All workflows", to: "/settings/workflow" },
                            {
                                label: workflow.name,
                                to: `/settings/workflow/${workflow.id}`,
                            },
                        ]}
                        current="Edit"
                    />
                    <div className="flex justify-between items-center mt-1">
                        <ContentHeader>
                            <span className="inline-flex items-center gap-2">
                                {workflow.name}
                                {isDraft ? (
                                    <Badge value="Draft" type="info" />
                                ) : (
                                    <Badge value="Active" type="success" />
                                )}
                            </span>
                        </ContentHeader>
                        <WorkflowStatusActions actions={actions} />
                    </div>
                </div>
                {!editable ? (
                    <InlineBanner type="info">
                        This workflow requires administrative access to additional{" "}
                        {productName} regions to make changes. Contact your agency
                        administrator or <SupportEmailLink /> to request changes.
                    </InlineBanner>
                ) : null}
                {resetNotice ? (
                    <InlineBanner type="info">{resetNotice}</InlineBanner>
                ) : null}
                <div className="max-w-3xl">
                    <WizardStepper
                        steps={WIZARD_STEPS}
                        currentIndex={stepIndex}
                        onSelect={handleStepperSelect}
                    />
                </div>
                <div className="max-w-3xl">
                    {stepIndex === 0 ? <WizardBasics {...stepProps} /> : null}
                    {stepIndex === 1 ? <WizardActions {...stepProps} /> : null}
                    {stepIndex === 2 ? <WizardReview {...stepProps} /> : null}
                </div>
                <div className="max-w-3xl flex items-center justify-between">
                    {stepIndex === 0 ? (
                        <Link to={`/settings/workflow/${workflow.id}`}>
                            <SecondaryButton>Back to workflow</SecondaryButton>
                        </Link>
                    ) : (
                        <SecondaryButton onClick={() => goToStep(stepIndex - 1, "back")}>
                            Back
                        </SecondaryButton>
                    )}
                    {stepIndex < WIZARD_STEPS.length - 1 ? (
                        <div className="flex flex-col items-end gap-1">
                            <PrimaryButton
                                onClick={() => goToStep(stepIndex + 1, "continue")}
                                disabled={!!continueBlocker}
                                title={continueBlocker ?? undefined}>
                                Continue &rarr;
                            </PrimaryButton>
                            {continueBlocker ? (
                                <p className="text-xs text-amber-600 text-right">
                                    {continueBlocker}
                                </p>
                            ) : null}
                        </div>
                    ) : isDraft && editable ? (
                        <div className="flex flex-col items-end gap-1">
                            <div className="flex items-center gap-2">
                                <Link to={`/settings/workflow/${workflow.id}`}>
                                    <SecondaryButton>Save as draft</SecondaryButton>
                                </Link>
                                <PrimaryButton
                                    onClick={handlePublishAndFinish}
                                    disabled={saving || !!publishBlocker}>
                                    Publish workflow
                                </PrimaryButton>
                            </div>
                            {publishBlocker ? (
                                <p className="text-xs text-amber-600 text-right">
                                    {publishBlocker}
                                </p>
                            ) : null}
                        </div>
                    ) : (
                        <Link to={`/settings/workflow/${workflow.id}`}>
                            <PrimaryButton>Done</PrimaryButton>
                        </Link>
                    )}
                </div>
            </div>
        </ContentFull>
    );
};
